import React, { useEffect, useMemo, useRef, useState } from "react";
import { PlusIcon, TagIcon } from "@heroicons/react/24/outline";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Modal,
  Popover,
  Spinner,
  TextArea,
} from "../../shared";
import { CheckboxList } from "../CheckboxList";
import useOutsideClick from "../../hooks/useOutsideClick";
import {
  useAddTagMutation,
  useArchiveTagMutation,
  useGetTagsQuery,
  useRestoreTagMutation,
  useUpdateTagMutation,
} from "../../redux/services/tags/tagsApi";
import { useUpdateJobMutation } from "../../redux/services/jobs/jobsApi";
import { useUpdateEquipmentMutation } from "../../redux/services/equipment";
import { useUpdateInvoiceV2Mutation } from "../../redux/services/invoices/invoicesV2Api";
import { getAppType } from "../../utils/authority";
import { getProductTerminology } from "../../constants/product-terminology";

const DEFAULT_COLOR = "#64748b";
const TAG_COLOR_PRESETS = [
  "#64748b",
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#22c55e",
  "#14b8a6",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
];

const TAG_PICKER_FLOATING_OPTIONS = {
  whileElementsMounted: autoUpdate,
  placement: "bottom-end",
  strategy: "fixed",
  middleware: [
    offset(8),
    flip({ allowedPlacements: ["bottom-end", "bottom-start", "top-end", "top-start"] }),
    shift({ padding: 8 }),
  ],
};

/**
 * Canonical string id for tag lookups (ObjectId, string, populated ref, Mongo extended JSON).
 * @param {unknown} value - Tag document, id string, or ObjectId
 * @returns {string}
 */
function normalizeTagId(value) {
  if (value == null || value === "") return "";
  const t = typeof value;
  if (t === "string" || t === "number") return String(value);
  if (t === "object") {
    if (Array.isArray(value)) return "";
    if (value.$oid != null) return String(value.$oid);
    if (value._id != null) return normalizeTagId(value._id);
    if (value.id != null) return normalizeTagId(value.id);
    if (typeof value.toString === "function") {
      try {
        const s = value.toString();
        if (/^[a-f\d]{24}$/i.test(s)) return s;
      } catch (e) {
        /* ignore */
      }
    }
  }
  return "";
}

const getTagId = (tag) => normalizeTagId(tag);

export const normalizeSelectedTagIds = (tags = []) =>
  Array.isArray(tags) ? tags.map((item) => normalizeTagId(item)).filter(Boolean) : [];

/**
 * Build a Map of tag id -> tag document from the tenant tag library query.
 * @param {Array} tagList - Tags from useGetTagsQuery
 * @returns {Map<string, object>}
 */
function buildTagLibraryMap(tagList = []) {
  const map = new Map();
  tagList.forEach((tag) => {
    const id = normalizeTagId(tag);
    if (id && tag && typeof tag === "object") map.set(id, tag);
  });
  return map;
}

/**
 * Map of id -> tag document from job.tags (only populated objects, not raw id strings).
 * @param {Array} jobTags - job.tags from API
 * @returns {Map<string, object>}
 */
function buildJobPopulatedTagMap(jobTags = []) {
  const map = new Map();
  (jobTags || []).forEach((raw) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;
    const id = normalizeTagId(raw);
    if (id) map.set(id, raw);
  });
  return map;
}

/**
 * Resolve assigned tag ids to full tag documents for display.
 * @param {string[]} selectedIds - Ordered tag ids on the job
 * @param {object[]} libraryTags - Tenant tag list
 * @param {Array} jobTags - job.tags (may be ids only or populated)
 * @returns {object[]}
 */
function resolveTagsForDisplay(selectedIds, libraryTags, jobTags) {
  const libraryMap = buildTagLibraryMap(libraryTags);
  const populatedFromJob = buildJobPopulatedTagMap(jobTags);
  return selectedIds
    .map((id) => libraryMap.get(id) || populatedFromJob.get(id))
    .filter((tag) => tag && typeof tag === "object");
}

const isHexColor = (value = "") => /^#[0-9a-fA-F]{6}$/.test(value);

const TagColorDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [hexDraft, setHexDraft] = useState(value || DEFAULT_COLOR);
  const ref = useRef(null);
  const selectedColor = isHexColor(value) ? value : DEFAULT_COLOR;

  useOutsideClick(ref, () => setOpen(false));

  function selectColor(color) {
    const nextColor = isHexColor(color) ? color : DEFAULT_COLOR;
    setHexDraft(nextColor);
    onChange(nextColor);
  }

  function handleHexChange(nextValue) {
    const normalized = nextValue.startsWith("#") ? nextValue : `#${nextValue}`;
    setHexDraft(normalized);
    if (isHexColor(normalized)) {
      onChange(normalized);
    }
  }

  return (
    <div className="relative" ref={ref}>
      <div className="mb-1 text-sm font-medium text-gray-900">Color</div>
      <button
        type="button"
        className="flex h-[42px] w-full items-center justify-between gap-3 rounded-lg border border-gray-300 bg-white px-3 text-left hover:bg-gray-50"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="flex min-w-0 items-center gap-3">
          <span
            className="h-7 w-7 rounded-full border border-gray-300 shadow-sm"
            style={{ backgroundColor: selectedColor }}
          />
          <span className="font-mono text-sm text-gray-700">{selectedColor}</span>
        </span>
        <span className="text-xs text-gray-500">{open ? "Close" : "Choose"}</span>
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Quick Select
          </div>
          <div className="grid grid-cols-9 gap-1.5">
            {TAG_COLOR_PRESETS.map((color) => {
              const isSelected = selectedColor.toLowerCase() === color.toLowerCase();
              return (
                <button
                  key={color}
                  type="button"
                  aria-label={`Use ${color}`}
                  className="h-7 w-7 rounded-full border border-gray-300 ring-offset-2"
                  style={{
                    backgroundColor: color,
                    boxShadow: isSelected ? "0 0 0 2px #2563eb" : undefined,
                  }}
                  onClick={() => selectColor(color)}
                />
              );
            })}
          </div>

          <div className="mt-4 border-t border-gray-100 pt-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Custom Color
            </div>
            <div className="flex items-center gap-3">
              <input
                aria-label="Custom tag color"
                className="h-10 w-12 cursor-pointer rounded border border-gray-300 bg-white p-1"
                type="color"
                value={selectedColor}
                onChange={(event) => selectColor(event.target.value)}
              />
              <Input
                aria-label="Tag color hex value"
                wrapperClasses="flex-1"
                className="font-mono"
                value={hexDraft}
                maxLength={7}
                placeholder="#64748b"
                onChange={(target) => handleHexChange(target.value || "")}
              />
            </div>
            {!isHexColor(hexDraft) && (
              <div className="mt-2 text-xs text-red-600">
                Use a 6-digit hex color, for example #64748b.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Centered empty-library prompt for the inline tag picker popover.
 * @param {object} props
 * @param {string} [props.entityLabel] - Job, invoice, equipment, or customer label
 * @param {() => void} props.onCreate - Opens the create-tag form
 * @param {boolean} [props.disabled]
 */
export const TagsPopoverEmptyState = ({
  entityLabel = "item",
  onCreate,
  disabled = false,
}) => {
  const lower = String(entityLabel || "item").toLowerCase();

  return (
    <div className="flex min-h-[16rem] w-full min-w-0 flex-col items-center justify-center overflow-hidden rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
      <TagIcon className="h-12 w-12 shrink-0 text-gray-400 stroke-1" aria-hidden />
      <h3 className="mt-3 w-full text-base font-semibold text-gray-900">
        No tags yet
      </h3>
      <p className="mt-1.5 w-full min-w-0 max-w-full text-pretty break-words text-sm text-gray-500">
        Create your first tag to organize this {lower}. You can reuse it anywhere
        tags are supported.
      </p>
      <Button
        size="sm"
        className="mt-5 max-w-full"
        style={{ minInlineSize: 0 }}
        disabled={disabled}
        icon={<PlusIcon className="mr-1.5 h-4 w-4" aria-hidden />}
        onClick={onCreate}
      >
        Create Tag
      </Button>
    </div>
  );
};

/**
 * Shared dropdown body for assigning and creating tags on an entity.
 * @param {object} props
 * @param {string} props.label - Entity label shown in the header and empty copy
 * @param {boolean} [props.isLoading]
 * @param {object[]} [props.tags]
 * @param {string[]} [props.selectedIds]
 * @param {string} props.checkboxIdPrefix
 * @param {boolean} [props.saving]
 * @param {boolean} [props.creating]
 * @param {boolean} [props.showCreateForm]
 * @param {() => void} props.onToggleCreateForm
 * @param {(tag: object) => void} props.onToggleTag
 * @param {string} [props.newTagName]
 * @param {(name: string) => void} props.onNewTagNameChange
 * @param {string} [props.newTagColor]
 * @param {(color: string) => void} props.onNewTagColorChange
 * @param {() => void} props.onCreateAndAssign
 * @param {string} [props.createPlaceholder]
 * @param {string} [props.className]
 * @param {React.CSSProperties} [props.style]
 */
export const TagsPickerMenu = React.forwardRef(function TagsPickerMenu(
  {
    label,
    isLoading = false,
    tags = [],
    selectedIds = [],
    checkboxIdPrefix,
    saving = false,
    creating = false,
    showCreateForm = false,
    onToggleCreateForm,
    onToggleTag,
    newTagName = "",
    onNewTagNameChange,
    newTagColor = DEFAULT_COLOR,
    onNewTagColorChange,
    onCreateAndAssign,
    createPlaceholder = "New tag name",
    className = "",
    style,
  },
  ref
) {
  const isEmptyLibrary = !isLoading && tags.length === 0;
  const busy = saving || creating;
  const menuStyle = {
    ...style,
    ...(isEmptyLibrary
      ? {
          width: "20rem",
          minWidth: "20rem",
          maxWidth: "min(20rem, calc(100vw - 1rem))",
        }
      : {}),
  };

  return (
    <div
      ref={ref}
      style={menuStyle}
      className={`w-80 overflow-hidden rounded-xl border border-gray-200 bg-white p-3 shadow-lg ${className}`}
    >
      {isEmptyLibrary && !showCreateForm ? (
        <TagsPopoverEmptyState
          entityLabel={label}
          onCreate={onToggleCreateForm}
          disabled={busy}
        />
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900">
              {isEmptyLibrary ? "Create Tag" : `${label} Tags`}
            </div>
            <div className="flex items-center gap-2">
              {busy && <Spinner size="sm" />}
              <Button
                size="xs"
                type={showCreateForm ? "primary" : "alternative"}
                disabled={busy}
                onClick={onToggleCreateForm}
              >
                {showCreateForm ? "Hide" : "Create"}
              </Button>
            </div>
          </div>

          {!isEmptyLibrary && (
            <div className="max-h-48 overflow-y-auto border-b border-gray-100 pb-3">
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Spinner />
                </div>
              ) : (
                <div className="space-y-2">
                  {tags.map((tag) => (
                    <Checkbox
                      key={tag._id}
                      id={`${checkboxIdPrefix}-${tag._id}`}
                      label={tag.name}
                      checked={selectedIds.includes(getTagId(tag))}
                      disabled={busy}
                      onChange={() => onToggleTag(tag)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {showCreateForm && (
            <div
              className={`space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 ${
                isEmptyLibrary ? "" : "mt-3"
              }`}
            >
              <Input
                label="Tag Name"
                placeholder={createPlaceholder}
                value={newTagName}
                onChange={(target) => onNewTagNameChange(target.value || "")}
              />
              <TagColorDropdown value={newTagColor} onChange={onNewTagColorChange} />
              <Button
                size="sm"
                className="w-full"
                loading={creating || saving}
                disabled={busy}
                onClick={onCreateAndAssign}
              >
                Create and Add
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export const TagBadgeList = ({
  tags = [],
  emptyText = "No tags",
  size = "sm",
  onRemoveTag,
}) => {
  const resolvedTags = Array.isArray(tags) ? tags.filter(Boolean) : [];
  const textClasses = size === "lg" ? "text-sm font-medium" : "text-xs font-semibold";
  const spacingClasses = onRemoveTag ? "pl-2.5 pr-1" : "px-2.5";

  if (resolvedTags.length === 0) {
    return <span className="text-sm text-gray-500">{emptyText}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {resolvedTags.map((tag) => (
        <span
          key={getTagId(tag)}
          className={`inline-flex items-center rounded border py-0.5 ${spacingClasses} ${textClasses}`}
          style={{
            borderColor: tag.color || DEFAULT_COLOR,
            color: tag.color || DEFAULT_COLOR,
            backgroundColor: `${tag.color || DEFAULT_COLOR}1A`,
          }}
        >
          {tag.name || tag.slug || "Tag"}
          {onRemoveTag && (
            <button
              type="button"
              className="ml-0.5 inline-flex h-4 w-4 items-center justify-center text-xs leading-none"
              aria-label={`Remove ${tag.name || "tag"}`}
              onClick={(event) => {
                event.stopPropagation();
                onRemoveTag(tag);
              }}
            >
              ×
            </button>
          )}
        </span>
      ))}
    </div>
  );
};

export const TagsFilterList = ({ appliesTo = "jobs", ...props }) => {
  const { data, isLoading } = useGetTagsQuery({ appliesTo, limit: 200 });
  const tags = data?.tags || [];

  function handleChange(val) {
    if (!val || val.length === 0) {
      props.onChange({
        title: props.value?.defaultText || "",
        value: "",
      });
      return;
    }

    props.onChange({
      title: val.map(({ name }) => name).join(","),
      value: val
        .map((tag) => normalizeTagId(tag))
        .filter(Boolean)
        .join(","),
    });
  }

  return (
    <CheckboxList
      onChange={handleChange}
      objectKey="_id"
      value={props.value.value}
      data={tags}
      loading={isLoading}
    />
  );
};

/** Tag filter dropdown for equipment list (`GET /equipment?tags=`). */
export const EquipmentTagsFilterList = (props) => (
  <TagsFilterList {...props} appliesTo="equipment" />
);

export const JobTagsEditor = ({ job = {}, form, editing = false }) => {
  const { data, isLoading } = useGetTagsQuery({ appliesTo: "jobs", limit: 200 });
  const tags = data?.tags || [];
  const selectedIds = normalizeSelectedTagIds(form?.form?.tags || job.tags);
  const selectedTags = useMemo(
    () => resolveTagsForDisplay(selectedIds, tags, job.tags),
    [job.tags, selectedIds, tags]
  );

  function handleTagToggle(tag, checked) {
    const tid = normalizeTagId(tag);
    const next = checked
      ? Array.from(new Set([...selectedIds, tid]))
      : selectedIds.filter((id) => id !== tid);

    form?.updateForm?.({ id: "tags", value: next });
  }

  if (!editing) {
    return <TagBadgeList tags={selectedTags} />;
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-800">Tags</div>
      {isLoading ? (
        <Spinner />
      ) : tags.length === 0 ? (
        <div className="text-sm text-gray-500">
          No tags have been created yet. Create tags from Settings.
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {tags.map((tag) => (
            <Checkbox
              key={tag._id}
              id={`job-tag-${tag._id}`}
              label={tag.name}
              checked={selectedIds.includes(normalizeTagId(tag))}
              onChange={(e) => handleTagToggle(tag, e.checked)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const EntityTagsInline = ({
  entity = {},
  appliesTo = "jobs",
  entityLabel,
  editable = true,
  onChangeTags,
  badgeSize = "sm",
  summarizeOverflow = false,
}) => {
  const label =
    entityLabel || appliesTo.charAt(0).toUpperCase() + appliesTo.slice(1);
  const [open, setOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [tagToRemove, setTagToRemove] = useState(null);
  const [removingTag, setRemovingTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(DEFAULT_COLOR);
  const ref = useRef(null);
  const { refs, floatingStyles } = useFloating(TAG_PICKER_FLOATING_OPTIONS);
  const { data, isLoading } = useGetTagsQuery({ appliesTo, limit: 200 });
  const [addTag, { isLoading: creating }] = useAddTagMutation();
  const [updateJob, { isLoading: savingJob }] = useUpdateJobMutation();
  const [updateEquipment, { isLoading: savingEquipment }] =
    useUpdateEquipmentMutation();
  const saving = savingJob || savingEquipment;
  const tags = data?.tags || [];
  const selectedIds = normalizeSelectedTagIds(entity.tags);

  useOutsideClick(ref, () => setOpen(false));

  const selectedTags = useMemo(
    () => resolveTagsForDisplay(selectedIds, tags, entity.tags),
    [entity.tags, selectedIds, tags]
  );

  /** Paired slices for summarizeOverflow so the first badge and "+N" always stay in sync. */
  const { tableVisibleTags, tableOverflowTags } = useMemo(() => {
    if (!summarizeOverflow) {
      return { tableVisibleTags: selectedTags, tableOverflowTags: [] };
    }
    return {
      tableVisibleTags: selectedTags.slice(0, 1),
      tableOverflowTags: selectedTags.slice(1),
    };
  }, [summarizeOverflow, selectedTags]);

  useEffect(() => {
    if (!open) {
      setShowCreateForm(false);
      setNewTagName("");
      setNewTagColor(DEFAULT_COLOR);
    }
  }, [open]);

  async function saveTags(nextTagIds) {
    if (!entity?._id) return;

    if (typeof onChangeTags === "function") {
      await onChangeTags(nextTagIds);
      return;
    }

    if (appliesTo === "equipment") {
      await updateEquipment({ id: entity._id, tags: nextTagIds }).unwrap();
      return;
    }

    await updateJob({
      id: entity._id,
      data: {
        tags: nextTagIds,
      },
    }).unwrap();
  }

  async function toggleTag(tag) {
    const tagId = getTagId(tag);
    const nextTagIds = selectedIds.includes(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : Array.from(new Set([...selectedIds, tagId]));

    try {
      await saveTags(nextTagIds);
    } catch (error) {
      toast(error?.data?.message || `Failed to update ${label.toLowerCase()} tags`, {
        type: "error",
        toastId: "entity-tags-update-error",
      });
    }
  }

  async function removeTagFromJob() {
    if (!tagToRemove) return;
    const tagId = getTagId(tagToRemove);
    const nextTagIds = selectedIds.filter((id) => id !== tagId);

    try {
      setRemovingTag(true);
      await saveTags(nextTagIds);
      setTagToRemove(null);
    } catch (error) {
      toast(error?.data?.message || `Failed to remove ${label.toLowerCase()} tag`, {
        type: "error",
        toastId: "entity-tags-remove-error",
      });
    } finally {
      setRemovingTag(false);
    }
  }

  async function createAndAssignTag() {
    const name = newTagName.trim();
    if (!name) {
      toast("Tag name is required", {
        type: "error",
        toastId: "inline-tag-name-required",
      });
      return;
    }

    try {
      const response = await addTag({
        name,
        color: newTagColor || DEFAULT_COLOR,
        appliesTo: [appliesTo],
      }).unwrap();
      const createdTagId = getTagId(response?.tag);
      if (createdTagId) {
        await saveTags(Array.from(new Set([...selectedIds, createdTagId])));
      }
      setNewTagName("");
      setNewTagColor(DEFAULT_COLOR);
      setShowCreateForm(false);
    } catch (error) {
      toast(error?.data?.message || "Failed to create tag", {
        type: "error",
        toastId: "inline-tag-create-error",
      });
    }
  }

  return (
    <div className="relative ml-2 flex min-w-0 items-center gap-1.5" ref={ref}>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <TagBadgeList
          tags={tableVisibleTags}
          emptyText=""
          size={badgeSize}
          onRemoveTag={editable ? setTagToRemove : undefined}
        />
        {tableOverflowTags.length > 0 && (
          <Popover
            position="top"
            offset={[-12, 0]}
            trigger={
              <Badge
                color="gray"
                label={`+${tableOverflowTags.length}`}
                className="shrink-0 cursor-pointer hover:bg-gray-200"
              />
            }
          >
            <div className="space-y-1 p-2">
              {tableOverflowTags.map((tag) => (
                <div
                  key={getTagId(tag)}
                  className="whitespace-nowrap text-sm text-gray-800"
                >
                  {tag.name || tag.slug || "Tag"}
                </div>
              ))}
            </div>
          </Popover>
        )}
      </div>
      {editable && (
        <button
          ref={refs.setReference}
          type="button"
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-dashed border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-600 hover:border-primary-300 hover:text-primary-700"
          aria-label={`Add or create ${label.toLowerCase()} tags`}
          title="Add or create tags"
          disabled={saving || creating}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="text-sm font-bold leading-none text-gray-500" aria-hidden>
            +
          </span>
          <span>Tags</span>
        </button>
      )}

      {open && (
        <TagsPickerMenu
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-[9999]"
          label={label}
          isLoading={isLoading}
          tags={tags}
          selectedIds={selectedIds}
          checkboxIdPrefix={`inline-${appliesTo}-tag`}
          saving={saving}
          creating={creating}
          showCreateForm={showCreateForm}
          onToggleCreateForm={() => setShowCreateForm((prev) => !prev)}
          onToggleTag={toggleTag}
          newTagName={newTagName}
          onNewTagNameChange={setNewTagName}
          newTagColor={newTagColor}
          onNewTagColorChange={setNewTagColor}
          onCreateAndAssign={createAndAssignTag}
        />
      )}
      <Modal
        visible={!!tagToRemove}
        setVisible={(visible) => {
          if (!visible) setTagToRemove(null);
        }}
        title="Remove Tag"
        size="sm"
        content={
          <div className="text-sm text-gray-700">
            Remove{" "}
            <span className="font-semibold text-gray-900">
              {tagToRemove?.name || "this tag"}
            </span>{" "}
            from this {label.toLowerCase()}?
          </div>
        }
        footer={
          <>
            <Button type="alternative" onClick={() => setTagToRemove(null)}>
              Cancel
            </Button>
            <Button
              type="danger"
              loading={removingTag || saving}
              disabled={removingTag || saving}
              onClick={removeTagFromJob}
            >
              Remove
            </Button>
          </>
        }
      />
    </div>
  );
};

/** @deprecated Use EntityTagsInline — kept for job screens. */
export const JobTagsInline = (props) => {
  const terms = getProductTerminology(getAppType());
  return (
    <EntityTagsInline
      appliesTo="jobs"
      entityLabel={terms.job}
      entity={props.job}
      {...props}
    />
  );
};

/**
 * Checkbox grid for invoice V2 draft/edit forms (controlled tag id list).
 * @param {object} props
 * @param {string[]} props.tagIds - Selected tag ids
 * @param {Function} props.onChange - (nextIds: string[]) => void
 */
export const InvoiceTagsField = ({ tagIds = [], onChange }) => {
  const { data, isLoading } = useGetTagsQuery({ appliesTo: "invoices", limit: 200 });
  const tags = data?.tags || [];
  const selectedIds = normalizeSelectedTagIds(tagIds);

  function handleTagToggle(tag, checked) {
    const tid = normalizeTagId(tag);
    const next = checked
      ? Array.from(new Set([...selectedIds, tid]))
      : selectedIds.filter((id) => id !== tid);
    onChange?.(next);
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-800">Tags</div>
      {isLoading ? (
        <Spinner />
      ) : tags.length === 0 ? (
        <div className="text-sm text-gray-500">
          No invoice tags yet. Create tags in Settings with &quot;invoices&quot; enabled.
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {tags.map((tag) => (
            <Checkbox
              key={tag._id}
              id={`invoice-v2-form-tag-${tag._id}`}
              label={tag.name}
              checked={selectedIds.includes(normalizeTagId(tag))}
              onChange={(e) => handleTagToggle(tag, e.checked)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const InvoiceTagsInline = ({
  invoice = {},
  editable = true,
  onChangeTags,
  badgeSize = "sm",
  summarizeOverflow = false,
}) => {
  const [open, setOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [tagToRemove, setTagToRemove] = useState(null);
  const [removingTag, setRemovingTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(DEFAULT_COLOR);
  const ref = useRef(null);
  const { refs, floatingStyles } = useFloating(TAG_PICKER_FLOATING_OPTIONS);
  const { data, isLoading } = useGetTagsQuery({ appliesTo: "invoices", limit: 200 });
  const [addTag, { isLoading: creating }] = useAddTagMutation();
  const [updateInvoiceV2, { isLoading: saving }] = useUpdateInvoiceV2Mutation();
  const tags = data?.tags || [];
  const selectedIds = normalizeSelectedTagIds(invoice.tags);

  useOutsideClick(ref, () => setOpen(false));

  const selectedTags = useMemo(
    () => resolveTagsForDisplay(selectedIds, tags, invoice.tags),
    [invoice.tags, selectedIds, tags]
  );

  const { tableVisibleTags, tableOverflowTags } = useMemo(() => {
    if (!summarizeOverflow) {
      return { tableVisibleTags: selectedTags, tableOverflowTags: [] };
    }
    return {
      tableVisibleTags: selectedTags.slice(0, 1),
      tableOverflowTags: selectedTags.slice(1),
    };
  }, [summarizeOverflow, selectedTags]);

  useEffect(() => {
    if (!open) {
      setShowCreateForm(false);
      setNewTagName("");
      setNewTagColor(DEFAULT_COLOR);
    }
  }, [open]);

  async function saveTags(nextTagIds) {
    // Draft / create: parent holds tag ids via onChangeTags (no invoice _id yet).
    if (typeof onChangeTags === "function") {
      await onChangeTags(nextTagIds);
      return;
    }

    if (!invoice?._id) return;

    await updateInvoiceV2({
      id: invoice._id,
      body: { tags: nextTagIds },
    }).unwrap();
  }

  async function toggleTag(tag) {
    const tagId = getTagId(tag);
    const nextTagIds = selectedIds.includes(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : Array.from(new Set([...selectedIds, tagId]));

    try {
      await saveTags(nextTagIds);
    } catch (error) {
      toast(error?.data?.message || "Failed to update invoice tags", {
        type: "error",
        toastId: "invoice-v2-tags-update-error",
      });
    }
  }

  async function removeTagFromInvoice() {
    if (!tagToRemove) return;
    const tagId = getTagId(tagToRemove);
    const nextTagIds = selectedIds.filter((id) => id !== tagId);

    try {
      setRemovingTag(true);
      await saveTags(nextTagIds);
      setTagToRemove(null);
    } catch (error) {
      toast(error?.data?.message || "Failed to remove invoice tag", {
        type: "error",
        toastId: "invoice-v2-tags-remove-error",
      });
    } finally {
      setRemovingTag(false);
    }
  }

  async function createAndAssignTag() {
    const name = newTagName.trim();
    if (!name) {
      toast("Tag name is required", {
        type: "error",
        toastId: "invoice-inline-tag-name-required",
      });
      return;
    }

    try {
      const response = await addTag({
        name,
        color: newTagColor || DEFAULT_COLOR,
        appliesTo: ["invoices"],
      }).unwrap();
      const createdTagId = getTagId(response?.tag);
      if (createdTagId) {
        await saveTags(Array.from(new Set([...selectedIds, createdTagId])));
      }
      setNewTagName("");
      setNewTagColor(DEFAULT_COLOR);
      setShowCreateForm(false);
    } catch (error) {
      toast(error?.data?.message || "Failed to create tag", {
        type: "error",
        toastId: "invoice-inline-tag-create-error",
      });
    }
  }

  return (
    <div className="relative ml-2 flex min-w-0 items-center gap-1.5" ref={ref}>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <TagBadgeList
          tags={tableVisibleTags}
          emptyText=""
          size={badgeSize}
          onRemoveTag={editable ? setTagToRemove : undefined}
        />
        {tableOverflowTags.length > 0 && (
          <Popover
            position="top"
            offset={[-12, 0]}
            trigger={
              <Badge
                color="gray"
                label={`+${tableOverflowTags.length}`}
                className="shrink-0 cursor-pointer hover:bg-gray-200"
              />
            }
          >
            <div className="space-y-1 p-2">
              {tableOverflowTags.map((tag) => (
                <div
                  key={getTagId(tag)}
                  className="whitespace-nowrap text-sm text-gray-800"
                >
                  {tag.name || tag.slug || "Tag"}
                </div>
              ))}
            </div>
          </Popover>
        )}
      </div>
      {editable && (
        <button
          ref={refs.setReference}
          type="button"
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-dashed border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-600 hover:border-primary-300 hover:text-primary-700"
          aria-label="Add or create invoice tags"
          title="Add or create invoice tags"
          disabled={saving || creating}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="text-sm font-bold leading-none text-gray-500" aria-hidden>
            +
          </span>
          <span>Tags</span>
        </button>
      )}

      {open && (
        <TagsPickerMenu
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-[9999]"
          label="Invoice"
          isLoading={isLoading}
          tags={tags}
          selectedIds={selectedIds}
          checkboxIdPrefix="inline-invoice-v2-tag"
          saving={saving}
          creating={creating}
          showCreateForm={showCreateForm}
          onToggleCreateForm={() => setShowCreateForm((prev) => !prev)}
          onToggleTag={toggleTag}
          newTagName={newTagName}
          onNewTagNameChange={setNewTagName}
          newTagColor={newTagColor}
          onNewTagColorChange={setNewTagColor}
          onCreateAndAssign={createAndAssignTag}
        />
      )}
      <Modal
        visible={!!tagToRemove}
        setVisible={(visible) => {
          if (!visible) setTagToRemove(null);
        }}
        title="Remove Tag"
        size="sm"
        content={
          <div className="text-sm text-gray-700">
            Remove{" "}
            <span className="font-semibold text-gray-900">
              {tagToRemove?.name || "this tag"}
            </span>{" "}
            from this invoice?
          </div>
        }
        footer={
          <>
            <Button type="alternative" onClick={() => setTagToRemove(null)}>
              Cancel
            </Button>
            <Button
              type="danger"
              loading={removingTag || saving}
              disabled={removingTag || saving}
              onClick={removeTagFromInvoice}
            >
              Remove
            </Button>
          </>
        }
      />
    </div>
  );
};

export const TagManagementPanel = ({
  appliesTo = "jobs",
  title = "Create or Edit Tag",
  description = "Tags are shared labels for organizing jobs across dispatch, billing, and follow-up workflows.",
  emptyText = "Create your first tag to start organizing jobs.",
  placeholder = "Waiting on Parts",
}) => {
  const [draft, setDraft] = useState({
    name: "",
    color: DEFAULT_COLOR,
    description: "",
  });
  const [editingTagId, setEditingTagId] = useState(null);
  const { data, isLoading } = useGetTagsQuery({
    appliesTo,
    includeArchived: true,
    limit: 200,
  });
  const [addTag, { isLoading: adding }] = useAddTagMutation();
  const [updateTag, { isLoading: updating }] = useUpdateTagMutation();
  const [archiveTag, { isLoading: archiving }] = useArchiveTagMutation();
  const [restoreTag, { isLoading: restoring }] = useRestoreTagMutation();
  const tags = data?.tags || [];
  const saving = adding || updating;

  function resetDraft() {
    setDraft({ name: "", color: DEFAULT_COLOR, description: "" });
    setEditingTagId(null);
  }

  async function saveTag() {
    if (!draft.name.trim()) {
      toast("Tag name is required", { type: "error", toastId: "tag-name-required" });
      return;
    }

    try {
      const payload = {
        name: draft.name.trim(),
        color: draft.color || DEFAULT_COLOR,
        description: draft.description,
        appliesTo: [appliesTo],
      };
      const res = editingTagId
        ? await updateTag({ id: editingTagId, data: payload }).unwrap()
        : await addTag(payload).unwrap();

      toast(res.message || "Tag saved", {
        type: res.status || "success",
        toastId: `tag-save-${editingTagId || "new"}`,
      });
      resetDraft();
    } catch (error) {
      toast(error?.data?.message || "Failed to save tag", {
        type: "error",
        toastId: "tag-save-error",
      });
    }
  }

  function startEdit(tag) {
    setEditingTagId(tag._id);
    setDraft({
      name: tag.name || "",
      color: tag.color || DEFAULT_COLOR,
      description: tag.description || "",
    });
  }

  async function toggleArchived(tag) {
    try {
      const res = tag.isArchived
        ? await restoreTag(tag._id).unwrap()
        : await archiveTag(tag._id).unwrap();
      toast(res.message || "Tag updated", {
        type: res.status || "success",
        toastId: `tag-archive-${tag._id}`,
      });
    } catch (error) {
      toast(error?.data?.message || "Failed to update tag", {
        type: "error",
        toastId: "tag-archive-error",
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-4">
          <div className="text-base font-semibold text-gray-900">
            {title}
          </div>
          <div className="text-sm text-gray-500">
            {description}
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-[1fr_220px]">
          <Input
            label="Tag name"
            value={draft.name}
            placeholder={placeholder}
            onChange={(target) =>
              setDraft((prev) => ({ ...prev, name: target.value || "" }))
            }
          />
          <TagColorDropdown
            value={draft.color}
            onChange={(color) =>
              setDraft((prev) => ({
                ...prev,
                color,
              }))
            }
          />
          <div className="md:col-span-2">
            <TextArea
              label="Description"
              value={draft.description}
              rows={2}
              placeholder="Optional note for office staff"
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <Button size="sm" loading={saving} disabled={saving} onClick={saveTag}>
              {editingTagId ? "Update Tag" : "Create Tag"}
            </Button>
            {editingTagId && (
              <Button size="sm" type="alternative" onClick={resetDraft}>
                Cancel Edit
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3 text-sm font-semibold text-gray-900">
          Existing Tags
        </div>
        {isLoading ? (
          <Spinner />
        ) : tags.length === 0 ? (
          <div className="text-sm text-gray-500">
            {emptyText}
          </div>
        ) : (
          <div className="space-y-2">
            {tags.map((tag) => (
              <div
                key={tag._id}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3"
              >
                <div className="min-w-0">
                  <TagBadgeList tags={[tag]} />
                  {tag.description && (
                    <div className="mt-1 text-xs text-gray-500">
                      {tag.description}
                    </div>
                  )}
                  {tag.isArchived && (
                    <Badge color="gray" label="Archived" className="mt-2" />
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="xs"
                    type="alternative"
                    disabled={tag.isArchived}
                    onClick={() => startEdit(tag)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="xs"
                    type={tag.isArchived ? "success" : "danger"}
                    loading={archiving || restoring}
                    disabled={archiving || restoring}
                    onClick={() => toggleArchived(tag)}
                  >
                    {tag.isArchived ? "Restore" : "Archive"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const TagManagementModal = ({ visible, setVisible }) => {
  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
        title="Manage Tags"
      size="lg"
      hideFooter
      content={<TagManagementPanel />}
    />
  );
};

export default TagBadgeList;
